import { cookie } from '@/utils/cookie' // Ensure this utility exists

let socket = null
let reconnectAttempts = 0
const maxReconnectAttempts = 10
let heartbeatInterval = null
const heartbeatIntervalTime = 30000 // 30 seconds
const pendingMessages = [] // Message queue for pending messages

function initializeConnection (eventId, messageHandler) {
  const sessionId = cookie()
  const brandId = window.vueAppConfig.info.id ?? false;
  let websocketEndpoint = `wss://services.zakonyprolidi.cz/socket/event/page?event_id=${encodeURIComponent(
    eventId
  )}`
  if (brandId) {
    websocketEndpoint += `&brand_id=${encodeURIComponent(brandId)}`
  }
  if (sessionId) {
    websocketEndpoint += `&session_id=${encodeURIComponent(sessionId)}`
  }

  //console.log('Connecting to WebSocket endpoint:', websocketEndpoint) // Debug: Log the endpoint

  socket = new WebSocket(websocketEndpoint)

  socket.onopen = function () {
    console.log('WebSocket Connection opened')
    reconnectAttempts = 0 // Reset reconnection attempts on successful connection

    // Start sending heartbeats
    heartbeatInterval = setInterval(() => {
      sendHeartbeat()
    }, heartbeatIntervalTime)

    // Send any pending messages
    sendPendingMessages()
  }

  socket.onmessage = function (event) {
    //console.log('Received raw message:', event.data) // Debug: Log the raw message
    try {
      const message = JSON.parse(event.data)
      //   console.log('Parsed message:', message) // Debug: Log the parsed message
      if (messageHandler) {
        //  console.log('Calling messageHandler with:', message) // Debug: Log before calling the handler
        messageHandler(message)
      }
    } catch (error) {
      console.error(
        'Error parsing WebSocket message:',
        error,
        'Message:',
        event.data
      ) // Debug: Error details
    }
  }

  socket.onerror = function (error) {
    console.error('WebSocket Error:', error) // Debug: Log WebSocket errors
  }

  socket.onclose = function (event) {
    console.log('WebSocket Connection closed:', event) // Debug: Log close event details
    console.log(`Code: ${event.code}, Reason: ${event.reason}`) // Debug: Log close code and reason

    // Stop sending heartbeats
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }

    attemptReconnect(eventId, messageHandler) // Attempt to reconnect on close
  }

  // Optionally, handle the window closing event to send a disconnect message
  window.addEventListener('beforeunload', handleBeforeUnload)
}

function handleBeforeUnload () {
  if (socket && socket.readyState === WebSocket.OPEN) {
    const disconnectMessage = {
      method: 'disconnect',
      timestamp: Date.now()
    }
    socket.send(JSON.stringify(disconnectMessage))
  }
}

function sendHeartbeat () {
  if (socket && socket.readyState === WebSocket.OPEN) {
    const heartbeatMessage = {
      method: 'heartbeat',
      timestamp: Date.now()
    }
    socket.send(JSON.stringify(heartbeatMessage))
    //   console.log('Heartbeat sent at', new Date())
  } else {
    console.warn('Cannot send heartbeat, socket not open')
  }
}

function attemptReconnect (eventId, messageHandler) {
  if (reconnectAttempts < maxReconnectAttempts) {
    reconnectAttempts += 1

    const reconnectDelay = Math.min(5000 * reconnectAttempts, 30000) // Exponential backoff, capped at 30 seconds

    console.log(
      `Attempting to reconnect... (Attempt ${reconnectAttempts}/${maxReconnectAttempts})`
    )

    setTimeout(() => {
      // Ensure the socket is fully closed before attempting to reconnect
      if (socket.readyState === WebSocket.CLOSED) {
        console.log('Socket is closed, attempting to reconnect.') // Debug: Log reconnect attempt
        initializeConnection(eventId, messageHandler)
      } else if (socket.readyState !== WebSocket.CLOSING) {
        console.log(
          'Force closing the existing WebSocket connection before reconnecting.'
        )
        socket.close()
      }
    }, reconnectDelay)
  } else {
    console.error(
      'Max reconnect attempts reached. Could not re-establish WebSocket connection.'
    )
  }
}

function sendPendingMessages () {
  while (pendingMessages.length > 0) {
    const message = pendingMessages.shift()
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message))
      // console.log('Sent pending message:', message)
    } else {
      console.warn('Socket closed while sending pending messages.')
      break
    }
  }
}

export function initializeSocket (eventId, messageHandler) {
  if (!eventId) {
    console.error('Event ID is required to initialize WebSocket connection')
    return
  }

  if (
    !socket ||
    socket.readyState === WebSocket.CLOSED ||
    socket.readyState === WebSocket.CLOSING
  ) {
    initializeConnection(eventId, messageHandler)
  } else {
    console.log(
      'WebSocket connection is already established or being established.'
    )
    socket.onmessage = function (event) {
      // console.log('Received message:', event.data)
      try {
        const message = JSON.parse(event.data)
        if (messageHandler) {
          //   console.log('Calling messageHandler with:', message) // Debug: Log before calling the handler
          messageHandler(message)
        }
      } catch (error) {
        /*  console.error(
          'Error parsing WebSocket message:',
          error,
          'Message:',
          event.data
        ) */
      }
    }
  }
}

export function closeSocket () {
  if (socket) {
    socket.onclose = null // Prevent onclose from triggering reconnect attempts
    // Stop sending heartbeats
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
    socket.close()
    window.removeEventListener('beforeunload', handleBeforeUnload)
  }
}

export function sendUserAction (action, group, actionData = {}) {
  const message = {
    method: 'userAction',
    action: action,
    group: group,
    action_data: actionData
  }

  if (socket && socket.readyState === WebSocket.OPEN) {
    // Send the message to the server via WebSocket
    socket.send(JSON.stringify(message))
  } else {
    // Queue the message to send later
    pendingMessages.push(message)
    console.warn(
      `${action} ${group} WebSocket is not connected. Message queued.`
    )
  }
}
